import React, { useRef, useEffect } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Heading2, Link2, Minus } from "lucide-react";

/* Safe lightweight rich-text editor (reuses the same contentEditable/execCommand pattern used in
   Communications). HTML is sanitized server-side (nh3) before persistence. */
export function RichTextEditor({ value, onChange, testId = "rich-text-editor", minHeight = 180, placeholder }) {
  const el = useRef(null);
  const sync = () => onChange(el.current ? el.current.innerHTML : "");
  useEffect(() => {
    if (el.current && document.activeElement !== el.current && el.current.innerHTML !== (value || "")) {
      el.current.innerHTML = value || "";
    }
  }, [value]);
  const exec = (cmd, arg) => {
    el.current?.focus();
    try { document.execCommand("styleWithCSS", false, true); } catch { /* older browsers */ }
    document.execCommand(cmd, false, arg); sync();
  };
  const addLink = () => { const url = window.prompt("Link URL (https://…)"); if (url) exec("createLink", url); };
  const Btn = ({ onClick, title, children, tid }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title} data-testid={tid}
      className="h-8 w-8 flex items-center justify-center rounded hover:bg-slate-200 text-slate-600">{children}</button>
  );
  const Sep = () => <span className="w-px h-5 bg-slate-200 mx-0.5" />;
  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`${testId}-wrap`}>
      <div className="flex flex-wrap items-center gap-0.5 bg-slate-50 border-b px-1.5 py-1">
        <Btn tid={`${testId}-h2`} title="Heading" onClick={() => exec("formatBlock", "<h2>")}><Heading2 className="w-4 h-4" /></Btn>
        <Btn tid={`${testId}-p`} title="Normal text" onClick={() => exec("formatBlock", "<p>")}><span className="text-xs font-semibold">P</span></Btn>
        <Sep />
        <Btn tid={`${testId}-bold`} title="Bold" onClick={() => exec("bold")}><Bold className="w-4 h-4" /></Btn>
        <Btn tid={`${testId}-italic`} title="Italic" onClick={() => exec("italic")}><Italic className="w-4 h-4" /></Btn>
        <Btn tid={`${testId}-underline`} title="Underline" onClick={() => exec("underline")}><Underline className="w-4 h-4" /></Btn>
        <Sep />
        <Btn tid={`${testId}-ul`} title="Bulleted list" onClick={() => exec("insertUnorderedList")}><List className="w-4 h-4" /></Btn>
        <Btn tid={`${testId}-ol`} title="Numbered list" onClick={() => exec("insertOrderedList")}><ListOrdered className="w-4 h-4" /></Btn>
        <Sep />
        <Btn tid={`${testId}-link`} title="Insert link" onClick={addLink}><Link2 className="w-4 h-4" /></Btn>
        <Btn tid={`${testId}-hr`} title="Divider" onClick={() => exec("insertHorizontalRule")}><Minus className="w-4 h-4" /></Btn>
      </div>
      <div ref={el} contentEditable suppressContentEditableWarning data-testid={testId}
        onInput={sync} onBlur={sync} data-placeholder={placeholder || ""}
        style={{ minHeight }}
        className="overflow-auto p-3 text-sm leading-relaxed focus:outline-none bg-white [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:my-2 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-brand [&_a]:underline [&_hr]:my-3 empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400" />
    </div>
  );
}

/* Safe read-only renderer for admin-authored rich text. New values are sanitized server-side
   (nh3) on write; legacy plain-text values (no tags) are escaped by React and shown as paragraphs,
   so existing content keeps rendering correctly. */
export function RichTextView({ html, className = "" }) {
  if (!html) return null;
  const looksHtml = /<\/?[a-z][\s\S]*>/i.test(html);
  const rt = "[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:font-semibold [&_h3]:mt-2 [&_p]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1.5 [&_li]:my-0.5 [&_a]:text-brand [&_a]:underline [&_strong]:font-semibold [&_hr]:my-2";
  if (!looksHtml) {
    return (
      <div className={className} data-testid="rich-text-view">
        {html.split(/\n{2,}/).map((p, i) => <p key={i} className="my-1.5 whitespace-pre-line">{p}</p>)}
      </div>
    );
  }
  return <div className={`${rt} ${className}`} data-testid="rich-text-view" dangerouslySetInnerHTML={{ __html: html }} />;
}
